import { httpsCallable } from "firebase/functions";
import { functionsService } from "@/infrastructure/firebase/firebase";
import logger from "@/logger";

export type AdminCreateAttendanceMode = "futureBooking" | "retroAdd";
export type RetroStatus = "booked" | "attended" | "absent" | "cancelled";

export type AdminCreateAttendanceInput = {
    sessionId: string;
    targetUid: string;
    mode: AdminCreateAttendanceMode;
    /** Only when retroactively marking absent or cancelled. Stored on the attendance record. */
    absenceReason?: string;
    retroStatus?: RetroStatus;
};

export type AdminCreateAttendanceResult = {
    attendanceId: string;
    success: true;
};

export type SwapSessionInput = {
    attendanceId: string;
    newSessionId: string;
};

export type AdminSwapSessionResult = {
    attendanceId: string;
    isOverbooked: boolean;
    isSuccess: boolean;
    originalSessionScheduledAt: string;
    sessionScheduledAt: string;
};

export type RescheduleSessionInput = {
    newScheduledAt: string;
    sessionId: string;
};

export type RescheduleSessionResult = {
    attendanceUpdatedCount: number;
    scheduledAt: string;
    sessionId: string;
    success: true;
};

export type AdminRemoveAttendanceInput = {
    attendanceId: string;
    sessionId: string;
};

export type AdminRemoveAttendanceResult = {
    success: true;
};

export type AdminCancelBookingInput = {
    attendanceId: string;
    sessionId: string;
    absenceReason?: string;
};

export type AdminCancelBookingResult = {
    attendanceId: string;
    sessionId: string;
    sessionScheduledAt: string;
    success: true;
};

async function adminCreateAttendance(input: AdminCreateAttendanceInput): Promise<AdminCreateAttendanceResult> {
    logger.debug("[Booking] Admin create attendance", input);
    const adminCreateAttendanceFn = httpsCallable<AdminCreateAttendanceInput, AdminCreateAttendanceResult>(
        functionsService,
        "adminCreateAttendance"
    );
    const result = await adminCreateAttendanceFn(input);
    return result.data;
}

async function adminRemoveAttendance(input: AdminRemoveAttendanceInput): Promise<AdminRemoveAttendanceResult> {
    logger.debug("[Booking] Admin remove attendance", input);
    const fn = httpsCallable<AdminRemoveAttendanceInput, AdminRemoveAttendanceResult>(
        functionsService,
        "adminRemoveAttendance"
    );
    const result = await fn(input);
    return result.data;
}

async function adminSwapSession(input: SwapSessionInput): Promise<AdminSwapSessionResult> {
    logger.debug("[Booking] Admin swap session", input);
    const adminSwapSessionFn = httpsCallable<SwapSessionInput, AdminSwapSessionResult>(
        functionsService,
        "adminSwapSession"
    );
    const result = await adminSwapSessionFn(input);
    return result.data;
}

async function adminCancelBooking(input: AdminCancelBookingInput): Promise<AdminCancelBookingResult> {
    logger.debug("[Booking] Admin cancel booking", input);
    const fn = httpsCallable<AdminCancelBookingInput, AdminCancelBookingResult>(functionsService, "adminCancelBooking");
    const result = await fn(input);
    return result.data;
}

async function rescheduleSession(input: RescheduleSessionInput): Promise<RescheduleSessionResult> {
    logger.debug("[Booking] Reschedule session", input);
    const fn = httpsCallable<RescheduleSessionInput, RescheduleSessionResult>(functionsService, "rescheduleSession");
    const result = await fn(input);
    return result.data;
}

const bookingService = {
    adminCancelBooking,
    adminCreateAttendance,
    adminRemoveAttendance,
    adminSwapSession,
    rescheduleSession,
};

export default bookingService;
